/**
 * Deep links that open a diner's own AI assistant with the menu pre-loaded.
 *
 * Used by the experimental "AI assistant picker" landing page (ModelSelectLanding).
 * Each provider gets a prompt that points at the public plaintext menu endpoint
 * (`GET /api/menus/:menuId/plaintext`) so the assistant can fetch the menu itself.
 * Prefill via `?q=` is best-effort — some providers ignore it, which is why the
 * landing page also offers a copy-to-clipboard fallback.
 */

import { API_URL } from '../config';

export type AssistantProvider = 'chatgpt' | 'gemini' | 'claude';

export interface AssistantLink {
  provider: AssistantProvider;
  label: string;
  url: string;
}

/**
 * Absolute URL of the machine-readable menu for a given menu + language.
 */
export function plaintextMenuUrl(menuId: string, language?: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  // API_URL may be relative ("/api") in production — make it absolute so the link
  // is usable when handed to an external assistant.
  const base = API_URL.startsWith('http') ? API_URL : `${origin}${API_URL}`;
  const qs = language && language !== 'en' ? `?lang=${encodeURIComponent(language)}` : '';
  return `${base}/menus/${menuId}/plaintext${qs}`;
}

export function buildAssistantPrompt(menuUrl: string, restaurantName: string): string {
  const name = restaurantName?.trim() || 'this restaurant';
  return (
    `Here is the menu for ${name}: ${menuUrl}\n\n` +
    `Please read it, then help me decide what to order. ` +
    `Ask me about dietary restrictions, allergies, and preferences first.`
  );
}

export function buildAssistantLinks(menuId: string, restaurantName: string, language?: string): AssistantLink[] {
  const prompt = buildAssistantPrompt(plaintextMenuUrl(menuId, language), restaurantName);
  const q = encodeURIComponent(prompt);
  return [
    { provider: 'chatgpt', label: 'ChatGPT', url: `https://chatgpt.com/?q=${q}` },
    { provider: 'gemini', label: 'Gemini', url: `https://gemini.google.com/app?q=${q}` },
    { provider: 'claude', label: 'Claude', url: `https://claude.ai/new?q=${q}` },
  ];
}
